import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { getFirmStats } from "@/lib/api";

/**
 * Live "Active Firms" counter for the public LawFirms landing page.
 *
 * Acts as social proof + recency signal:
 *   - Active firms on HPM
 *   - States covered
 *   - Leads claimed in the last 30 days
 *
 * Polls /api/firms/stats every 30s while the component is mounted.
 */
export default function FirmsLiveCounter() {
    const [stats, setStats] = useState(null);
    const [pulse, setPulse] = useState(false);

    useEffect(() => {
        let active = true;
        const tick = async () => {
            try {
                const data = await getFirmStats();
                if (!active) return;
                setStats((prev) => {
                    if (prev && data.leads_claimed_last_30d > prev.leads_claimed_last_30d) {
                        setPulse(true);
                        setTimeout(() => setPulse(false), 2000);
                    }
                    return data;
                });
            } catch {
                // silent: this is decorative
            }
        };
        tick();
        const interval = setInterval(tick, 30000);
        return () => {
            active = false;
            clearInterval(interval);
        };
    }, []);

    if (!stats) {
        return (
            <div
                className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 flex items-center gap-2"
                data-testid="firms-live-counter-loading"
            >
                <Loader2 size={10} className="animate-spin" /> LOADING LIVE STATS…
            </div>
        );
    }

    const stat = (value, label, accent) => (
        <div className="flex items-baseline gap-2 min-w-0">
            <span
                className={`font-anton text-3xl sm:text-4xl leading-none ${
                    accent || "text-white"
                }`}
            >
                {value}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 truncate">
                {label}
            </span>
        </div>
    );

    return (
        <div
            className={`border-2 border-white/20 p-5 bg-white/5 flex flex-wrap items-center gap-x-10 gap-y-4 transition-colors duration-700 ${
                pulse ? "border-[#00FF85]" : ""
            }`}
            data-testid="firms-live-counter"
        >
            <div className="flex items-center gap-2 mr-2">
                <span
                    className={`w-2 h-2 rounded-full ${
                        pulse ? "bg-[#00FF85] animate-ping" : "bg-[#00FF85] animate-pulse"
                    }`}
                />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FF85]">
                    LIVE
                </span>
            </div>
            {stat(stats.active_firms, "FIRMS ON HPM", "text-white")}
            {stat(stats.states_covered, "STATES COVERED", "text-white")}
            {stat(stats.leads_claimed_last_30d, "LEADS CLAIMED · 30D", "text-[#FF3B30]")}
            {stats.blog_leads_last_30d > 0 &&
                stat(stats.blog_leads_last_30d, "BLOG LEADS · 30D", "text-[#FFCC00]")}
        </div>
    );
}
